import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "next-themes";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const LOGO =
  "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/ssol55p1_Logo%20bramsgraphic%20white.png";
const HERO_BG =
  "https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/df0b2sox_ChatGPT%20Image%2026%20juin%202026%2C%2018_46_45.png";

const ACCENT = "#E10600";

export default function Intro({ onEnter }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [closing, setClosing] = useState(false);

  // Lock body scroll while intro is visible
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const particles = useMemo(
    () =>
      Array.from({ length: 40 }).map((_, i) => {
        const size = 1 + Math.random() * 3;
        const left = Math.random() * 100;
        const delay = Math.random() * -35;
        const duration = 22 + Math.random() * 26;
        const opacity = 0.25 + Math.random() * 0.55;
        return { i, size, left, delay, duration, opacity };
      }),
    []
  );

  const handleEnter = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => onEnter?.(), 900);
  };

  return (
    <AnimatePresence>
      {!closing || closing ? (
        <motion.div
          key="intro"
          initial={{ opacity: 1 }}
          animate={{ opacity: closing ? 0 : 1, scale: closing ? 1.06 : 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className={cn(
            "fixed inset-0 z-[100] flex items-center justify-center overflow-hidden",
            isDark ? "bg-black" : "bg-white"
          )}
          data-testid="intro-screen"
        >
          {/* Background artwork */}
          <div className="absolute inset-0">
            <img
              src={HERO_BG}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center 20%" }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: isDark
                  ? "radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 70%, rgba(0,0,0,0.96) 100%)"
                  : "radial-gradient(ellipse at center, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.85) 70%, rgba(255,255,255,0.96) 100%)",
              }}
            />
          </div>

          {/* Animated blobs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="hero-blob-a absolute -top-1/4 -left-1/4 w-[70vw] h-[70vw] rounded-full"
              style={{
                background: `radial-gradient(closest-side, ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}, transparent 70%)`,
                filter: "blur(60px)",
              }}
            />
            <div
              className="hero-blob-b absolute top-1/4 -right-1/4 w-[60vw] h-[60vw] rounded-full"
              style={{
                background: `radial-gradient(closest-side, rgba(225,6,0,0.10), transparent 70%)`,
                filter: "blur(80px)",
              }}
            />
            <div
              className="hero-blob-c absolute -bottom-1/3 left-1/4 w-[55vw] h-[55vw] rounded-full"
              style={{
                background: `radial-gradient(closest-side, ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}, transparent 70%)`,
                filter: "blur(70px)",
              }}
            />
          </div>

          {/* Particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {particles.map((p) => (
              <span
                key={p.i}
                className="particle bg-white"
                style={{
                  left: `${p.left}%`,
                  bottom: "-12px",
                  width: `${p.size}px`,
                  height: `${p.size}px`,
                  animationDelay: `${p.delay}s`,
                  animationDuration: `${p.duration}s`,
                  opacity: isDark ? p.opacity : p.opacity * 0.45,
                  filter: "blur(0.5px)",
                }}
              />
            ))}
          </div>

          {/* Center content */}
          <div className="relative z-10 flex flex-col items-center text-center px-6">
            {/* Logo with glow */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="relative"
            >
              <div
                className="absolute inset-0 -m-16 rounded-full"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(225,6,0,0.18), transparent 65%)",
                  filter: "blur(40px)",
                }}
              />
              <motion.img
                src={LOGO}
                alt="Brams Graphic Design"
                className={cn(
                  "relative w-56 h-56 md:w-80 md:h-80 object-contain select-none",
                  isDark ? "" : "invert"
                )}
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                draggable={false}
                data-testid="intro-logo"
              />
            </motion.div>

            {/* Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.7 }}
              className="mt-10 md:mt-12 flex flex-col items-center gap-3"
            >
              <span
                className={cn(
                  "text-[10px] md:text-[11px] uppercase tracking-[0.5em]",
                  isDark ? "text-white/65" : "text-black/65"
                )}
              >
                Brams Graphic Design
              </span>
              <div className="flex items-center gap-3">
                <span className="h-px w-6" style={{ background: ACCENT }} />
                <span
                  className={cn(
                    "text-[10px] md:text-xs uppercase tracking-[0.4em]",
                    isDark ? "text-white/55" : "text-black/55"
                  )}
                >
                  Studio Indépendant
                </span>
                <span className="h-px w-6" style={{ background: ACCENT }} />
              </div>
            </motion.div>

            {/* Enter button */}
            <motion.button
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 1.0 }}
              onClick={handleEnter}
              data-testid="intro-enter-button"
              className={cn(
                "group relative mt-12 md:mt-16 inline-flex items-center gap-3 rounded-full pl-7 pr-3 py-3 font-medium text-sm tracking-wide transition-all duration-300 ease-in-out hover:scale-[1.03] active:scale-95 backdrop-blur-md border",
                isDark
                  ? "bg-white/10 border-white/20 text-white hover:bg-white/15"
                  : "bg-black/10 border-black/20 text-black hover:bg-black/15"
              )}
              style={{
                boxShadow: `0 0 60px -10px rgba(225,6,0,0.35)`,
              }}
            >
              <span className="uppercase tracking-[0.25em] text-xs">
                Cliquez pour entrer
              </span>
              <span
                className="flex items-center justify-center w-9 h-9 rounded-full transition-transform duration-500 group-hover:translate-x-1"
                style={{ background: ACCENT }}
              >
                <ArrowRight size={16} className="text-white" />
              </span>
              {/* Pulsing ring */}
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  boxShadow: `0 0 0 1px rgba(225,6,0,0.35)`,
                  animation: "intro-pulse 2.4s ease-out infinite",
                }}
              />
            </motion.button>

            {/* Bottom hint */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 1.4 }}
              className={cn(
                "absolute bottom-10 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.5em]",
                isDark ? "text-white/40" : "text-black/40"
              )}
            >
              · Bienvenue ·
            </motion.div>
          </div>

          {/* Scoped keyframes for the pulse */}
          <style>{`
            @keyframes intro-pulse {
              0%   { box-shadow: 0 0 0 1px rgba(225,6,0,0.45), 0 0 0 0 rgba(225,6,0,0.45); }
              70%  { box-shadow: 0 0 0 1px rgba(225,6,0,0.15), 0 0 0 16px rgba(225,6,0,0); }
              100% { box-shadow: 0 0 0 1px rgba(225,6,0,0.0),  0 0 0 0 rgba(225,6,0,0); }
            }
          `}</style>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
